import { useEffect, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { createFileRoute } from "@tanstack/react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ExpandableRow } from "@/components/ui/responsive-table";
import { useAdminFetch } from "@/hooks/use-admin-fetch";

/**
 * Admin → Medical Services.
 *
 * Port of `src/app/admin/services/services-client.tsx` (legacy NextJS, ~310 LOC) per D21.
 * AI-generated services per specialty + grouped table view.
 *
 * Endpoints:
 *   - GET  /api/admin/services?specialty=CODE
 *   - POST /api/admin/services/import (AI generate)
 *   - PUT  /api/admin/services (bulk insert confirmed)
 */

interface ServiceItem {
  id: string;
  code: string;
  name: Record<string, string> | string;
  category: string | null;
  defaultDuration: number;
  isTelemedicineSuitable: boolean;
  icon: string | null;
  /**
   * GET /api/admin/services returns the specialty only through the junction rows
   * (`serviceSpecialties: [{ specialty }]`) — there is no root-level `specialty`.
   * Typing it as one made the grouping reduce below dereference `undefined.code`,
   * so the page threw a TypeError and rendered blank on any non-empty DB.
   */
  serviceSpecialties: {
    specialty: { id?: string; code: string; name: Record<string, string> | string };
  }[];
  _count: { countryServices: number };
}

/** Group key for services whose specialty links are missing/empty. */
const UNASSIGNED = "__unassigned__";

/** A MedicalService can link several specialties; the first one drives grouping. */
function primarySpecialty(s: ServiceItem) {
  return s.serviceSpecialties?.[0]?.specialty;
}

interface ImportedService {
  code: string;
  specialtyCode: string;
  specialtyId: string;
  name: { ro: string; en: string };
  category: string;
  defaultDuration: number;
  isTelemedicineSuitable: boolean;
  alreadyExists: boolean;
}

const categoryColors: Record<string, string> = {
  consultation: "bg-blue-100 text-blue-700",
  procedure: "bg-purple-100 text-purple-700",
  diagnostic: "bg-green-100 text-green-700",
  lab: "bg-amber-100 text-amber-700",
  imaging: "bg-pink-100 text-pink-700",
};

function localized(field: Record<string, string> | string): { ro?: string; en?: string } {
  return typeof field === "string" ? JSON.parse(field) : field;
}

export const Route = createFileRoute("/_authenticated/services")({
  component: ServicesPage,
});

function ServicesPage() {
  const { t } = useLingui();
  const { adminFetch } = useAdminFetch();
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterSpecialty, setFilterSpecialty] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState<ImportedService[] | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const url = filterSpecialty
          ? `/api/admin/services?specialty=${filterSpecialty}`
          : "/api/admin/services";
        const data = await adminFetch.get<ServiceItem[]>(url);
        if (!cancelled) setServices(data);
      } catch {
        if (!cancelled) setServices([]);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [adminFetch, filterSpecialty]);

  const handleImport = async () => {
    setImporting(true);
    setImportResults(null);
    try {
      const body = filterSpecialty ? { specialtyCode: filterSpecialty } : {};
      const data = await adminFetch.post<{ services: ImportedService[] }>(
        "/api/admin/services/import",
        body,
      );
      setImportResults(data.services);
    } catch {
      /* ignore */
    }
    setImporting(false);
  };

  const confirmImport = async () => {
    if (!importResults) return;
    setSaving(true);
    try {
      const newServices = importResults
        .filter((s) => !s.alreadyExists)
        .map((s) => ({
          code: s.code,
          name: s.name,
          // The bulk PUT handler resolves specialties by code (specialtyCodes[] or
          // specialtyCode); a `specialtyId` is silently dropped, which created every
          // imported service with an empty serviceSpecialties link set.
          specialtyCodes: [s.specialtyCode],
          category: s.category,
          defaultDuration: s.defaultDuration,
          isTelemedicineSuitable: s.isTelemedicineSuitable,
        }));
      await adminFetch.put("/api/admin/services", { services: newServices });
      setImportResults(null);
      const refreshed = await adminFetch.get<ServiceItem[]>(
        filterSpecialty
          ? `/api/admin/services?specialty=${filterSpecialty}`
          : "/api/admin/services",
      );
      setServices(refreshed);
    } catch {
      /* ignore */
    }
    setSaving(false);
  };

  const filtered = services.filter((s) => {
    if (!search) return true;
    const q = search.toLowerCase();
    const name = localized(s.name);
    return (
      s.code.toLowerCase().includes(q) ||
      (name.en || "").toLowerCase().includes(q) ||
      (name.ro || "").toLowerCase().includes(q) ||
      s.serviceSpecialties?.some((link) => link.specialty.code.toLowerCase().includes(q))
    );
  });

  const grouped = filtered.reduce<Record<string, ServiceItem[]>>((acc, s) => {
    const key = primarySpecialty(s)?.code ?? UNASSIGNED;
    if (!acc[key]) acc[key] = [];
    acc[key].push(s);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">
          <Trans>Medical Services</Trans>
        </h1>
        <p className="text-muted-foreground text-sm">
          <Trans>Bookable services grouped by specialty</Trans>
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder={t`Filter by specialty code (e.g. CAR)...`}
          value={filterSpecialty}
          onChange={(e) => setFilterSpecialty(e.target.value.toUpperCase())}
          className="w-56"
        />

        <Badge variant="secondary">
          <Trans>{services.length} services</Trans>
        </Badge>

        <Button variant="outline" size="sm" onClick={handleImport} disabled={importing}>
          {importing ? (
            <Trans>Generating...</Trans>
          ) : filterSpecialty ? (
            <Trans>Generate Services (AI) for {filterSpecialty}</Trans>
          ) : (
            <Trans>Generate Services (AI)</Trans>
          )}
        </Button>

        <div className="ml-auto">
          <Input
            placeholder={t`Search...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-48"
          />
        </div>
      </div>

      {importResults && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950/30">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-semibold">
              <Trans>
                AI Generated — {importResults.length} services (
                {importResults.filter((s) => !s.alreadyExists).length} new)
              </Trans>
            </h3>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setImportResults(null)}>
                <Trans>Cancel</Trans>
              </Button>
              <Button size="sm" onClick={confirmImport} disabled={saving}>
                {saving ? <Trans>Saving...</Trans> : <Trans>Confirm Import</Trans>}
              </Button>
            </div>
          </div>
          <div className="max-h-80 space-y-1 overflow-y-auto">
            {importResults.map((s) => (
              <div
                key={s.code}
                className={`flex items-center justify-between rounded px-3 py-1.5 text-sm ${
                  s.alreadyExists
                    ? "bg-gray-100 dark:bg-gray-800"
                    : "bg-green-50 dark:bg-green-950/30"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-gray-400">{s.specialtyCode}</span>
                  <span className="font-medium">{s.name.ro || s.name.en}</span>
                  <Badge className={`text-[0.625rem] ${categoryColors[s.category] || ""}`}>
                    {s.category}
                  </Badge>
                  <span className="text-xs text-gray-400">{s.defaultDuration}min</span>
                </div>
                {s.alreadyExists && (
                  <Badge variant="outline" className="text-[0.625rem]">
                    <Trans>exists</Trans>
                  </Badge>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-12 text-center text-gray-400">
          <Trans>Loading...</Trans>
        </div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="py-12 text-center text-gray-400">
          <Trans>No services found. Click "Generate Services (AI)" to create them.</Trans>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([specCode, specServices]) => {
            const spec = specServices[0] ? primarySpecialty(specServices[0]) : undefined;
            const specName = localized(spec?.name ?? {});
            return (
              <div key={specCode} className="overflow-hidden rounded-xl border">
                <div className="flex items-center gap-2 border-b bg-gray-50 px-4 py-2.5 dark:bg-gray-900">
                  {spec ? (
                    <>
                      <span className="font-mono text-xs text-gray-400">{spec.code}</span>
                      <span className="font-semibold">{specName.en || specName.ro}</span>
                    </>
                  ) : (
                    <span className="font-semibold">
                      <Trans>Unassigned specialty</Trans>
                    </span>
                  )}
                  <Badge variant="secondary" className="text-[0.625rem]">
                    {specServices.length}
                  </Badge>
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    {specServices.map((s) => {
                      const name = localized(s.name);
                      return (
                        <ExpandableRow
                          key={s.id}
                          className="border-b last:border-0"
                          mobileDetails={[
                            {
                              label: t`Category`,
                              value: (
                                <Badge
                                  className={`text-[0.625rem] ${
                                    categoryColors[s.category || ""] || ""
                                  }`}
                                >
                                  {s.category}
                                </Badge>
                              ),
                            },
                            { label: t`Duration`, value: `${s.defaultDuration}min` },
                            {
                              label: t`Telemedicine`,
                              value: s.isTelemedicineSuitable ? t`Yes` : t`No`,
                            },
                            {
                              label: t`Countries`,
                              value: t`${s._count.countryServices} countries`,
                            },
                          ]}
                        >
                          <td className="w-36 px-4 py-2 font-mono text-xs text-gray-400">
                            {s.code}
                          </td>
                          <td className="px-4 py-2">{name.ro || name.en}</td>
                          <td className="hidden px-4 py-2 sm:table-cell">
                            <Badge
                              className={`text-[0.625rem] ${
                                categoryColors[s.category || ""] || ""
                              }`}
                            >
                              {s.category}
                            </Badge>
                          </td>
                          <td className="hidden px-4 py-2 text-xs text-gray-500 sm:table-cell">
                            {s.defaultDuration}min
                          </td>
                          <td className="hidden px-4 py-2 md:table-cell">
                            {s.isTelemedicineSuitable && (
                              <Badge variant="outline" className="text-[0.625rem]">
                                <Trans>telemedicine</Trans>
                              </Badge>
                            )}
                          </td>
                          <td className="hidden px-4 py-2 text-xs text-gray-400 md:table-cell">
                            <Trans>{s._count.countryServices} countries</Trans>
                          </td>
                        </ExpandableRow>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
